<div class="min-h-screen  text-slate-100 flex flex-col font-sans">

  <!-- TOP NAVIGATION HEADER -->
  <header class="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-30">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <div class="flex items-center gap-3">
          <div class="p-2 bg-blue-600/20 text-blue-400 rounded-lg border border-blue-500/30">
            <i data-lucide="anchor" class="w-6 h-6"></i>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <span class="font-bold text-lg tracking-tight text-white">NAVI<span class="text-blue-500">METRICS</span></span>
              <span class="px-2 py-0.5 text-xs font-semibold bg-blue-950 text-blue-400 border border-blue-800 rounded-full">Enterprise</span>
            </div>
            <p class="text-xs text-slate-400">Maritime Report & Intelligence Analytics</p>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-3">
          <button (click)="toggleApiModal(true)" class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition">
            <i data-lucide="code-2" class="w-4 h-4 text-emerald-400"></i>
            <span>API Schema Inspector</span>
          </button>
          <button (click)="triggerToast('Exporting CSV...')" class="hidden sm:flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition">
            <i data-lucide="download" class="w-4 h-4"></i>
            <span>Export CSV</span>
          </button>
          <button (click)="triggerToast('Generating Summary PDF...')" class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg shadow-lg transition">
            <i data-lucide="file-text" class="w-4 h-4"></i>
            <span>Executive Summary</span>
          </button>
        </div>
      </div>
    </div>
  </header>

  <!-- MAIN CONTAINER -->
  <main class="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

    <!-- GLOBAL FILTER BAR -->
    <section class="bg-slate-900 border border-slate-800 rounded-xl p-4 shadow-xl">
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        
        <!-- User Selector -->
        <div class="flex-1 min-w-[280px]">
          <label class="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Select User / Officer</label>
          <div class="relative">
            <button (click)="toggleUserDropdown()" type="button" class="w-full flex items-center justify-between bg-slate-950 border border-slate-700 hover:border-blue-500 text-left px-3.5 py-2 rounded-lg text-sm transition focus:outline-none">
              <div class="flex items-center gap-3 overflow-hidden">
                <img [src]="selectedUser.avatar" [alt]="selectedUser.name" class="w-7 h-7 rounded-full object-cover border border-slate-700">
                <div class="truncate text-left">
                  <div class="text-xs font-bold text-slate-100 leading-tight">{{ selectedUser.name }}</div>
                  <div class="text-[10px] text-slate-400 leading-tight">{{ selectedUser.role }} &bull; <span class="text-blue-400">{{ selectedUser.department }}</span></div>
                </div>
              </div>
              <i data-lucide="chevron-down" class="w-4 h-4 text-slate-400 flex-shrink-0"></i>
            </button>

            <!-- User Dropdown Menu -->
            <div *ngIf="isUserDropdownOpen" class="absolute left-0 right-0 mt-2 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden divide-y divide-slate-800">
              <div class="p-2">
                <input type="text" [(ngModel)]="userSearchQuery" placeholder="Search user or role..." class="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500">
              </div>
              <div class="max-h-60 overflow-y-auto p-1 space-y-0.5">
                <div *for="let user of filteredUsers" (click)="selectUser(user)" class="flex items-center justify-between p-2 hover:bg-slate-800 rounded-lg cursor-pointer transition">
                  <div class="flex items-center gap-3">
                    <img [src]="user.avatar" [alt]="user.name" class="w-8 h-8 rounded-full object-cover border border-slate-700">
                    <div>
                      <div class="text-xs font-semibold text-slate-100">{{ user.name }}</div>
                      <div class="text-[10px] text-slate-400">{{ user.role }}</div>
                    </div>
                  </div>
                  <i *ngIf="user.id === selectedUser.id" data-lucide="check" class="w-4 h-4 text-blue-400"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Time Range Selector -->
        <div class="flex flex-wrap items-center gap-3">
          <div>
            <label class="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Time Window</label>
            <div class="inline-flex bg-slate-950 p-1 rounded-lg border border-slate-800 text-xs">
              <button (click)="setTimeframe('7d')" [class]="selectedTimeframe === '7d' ? 'px-3 py-1.5 rounded-md text-white bg-blue-600 font-medium' : 'px-3 py-1.5 rounded-md text-slate-400 hover:text-white'">7 Days</button>
              <button (click)="setTimeframe('30d')" [class]="selectedTimeframe === '30d' ? 'px-3 py-1.5 rounded-md text-white bg-blue-600 font-medium' : 'px-3 py-1.5 rounded-md text-slate-400 hover:text-white'">30 Days</button>
              <button (click)="setTimeframe('90d')" [class]="selectedTimeframe === '90d' ? 'px-3 py-1.5 rounded-md text-white bg-blue-600 font-medium' : 'px-3 py-1.5 rounded-md text-slate-400 hover:text-white'">90 Days</button>
              <button (click)="setTimeframe('ytd')" [class]="selectedTimeframe === 'ytd' ? 'px-3 py-1.5 rounded-md text-white bg-blue-600 font-medium' : 'px-3 py-1.5 rounded-md text-slate-400 hover:text-white'">YTD</button>
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Quick Actions</label>
            <button (click)="resetFilters()" class="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs font-medium text-slate-300 rounded-lg transition flex items-center gap-1.5 h-[34px]">
              <i data-lucide="rotate-ccw" class="w-3.5 h-3.5"></i>
              <span>Reset</span>
            </button>
          </div>
        </div>

      </div>
    </section>

    <!-- KPI CARDS GRID -->
    <section class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      
      <!-- Total Output -->
      <div class="bg-slate-900 border border-slate-800 rounded-xl p-5 relative overflow-hidden group hover:border-slate-700 transition">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Output</span>
          <div class="p-2 bg-blue-500/10 text-blue-400 rounded-lg">
            <i data-lucide="file-bar-chart-2" class="w-5 h-5"></i>
          </div>
        </div>
        <div class="mt-3 flex items-baseline justify-between">
          <span class="text-3xl font-extrabold text-white">{{ currentAnalytics.summary.totalReports }}</span>
          <span [class]="currentAnalytics.summary.trendPercent >= 0 ? 'flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-400 border border-emerald-800/50' : 'flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-950 text-rose-400 border border-rose-800/50'">
            <i data-lucide="trending-up" class="w-3 h-3 mr-1"></i>
            <span>{{ currentAnalytics.summary.trendPercent }}%</span>
          </span>
        </div>
        <p class="text-xs text-slate-500 mt-2">vs. previous period</p>
      </div>

      <!-- Primary Module -->
      <div class="bg-slate-900 border border-slate-800 rounded-xl p-5 relative overflow-hidden group hover:border-slate-700 transition">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Primary Module</span>
          <div class="p-2 bg-purple-500/10 text-purple-400 rounded-lg">
            <i data-lucide="layers" class="w-5 h-5"></i>
          </div>
        </div>
        <div class="mt-3">
          <div class="text-xl font-bold text-white truncate">{{ getTopModuleName() }}</div>
          <div class="flex items-center justify-between mt-1">
            <span class="text-xs text-slate-400">{{ getModuleCount(getTopModuleName()) }} reports</span>
            <span class="text-xs font-semibold text-purple-400">{{ getTopModuleShare() }}% of total</span>
          </div>
        </div>
        <div class="w-full bg-slate-800 rounded-full h-1.5 mt-3">
          <div class="bg-purple-500 h-1.5 rounded-full" [style.width.%]="getTopModuleShare()"></div>
        </div>
      </div>

      <!-- Weekly Pace -->
      <div class="bg-slate-900 border border-slate-800 rounded-xl p-5 relative overflow-hidden group hover:border-slate-700 transition">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Weekly Pace</span>
          <div class="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg">
            <i data-lucide="zap" class="w-5 h-5"></i>
          </div>
        </div>
        <div class="mt-3 flex items-baseline justify-between">
          <span class="text-3xl font-extrabold text-white">{{ currentAnalytics.summary.avgWeekly }}</span>
          <span class="text-xs text-slate-400">reports / wk</span>
        </div>
        <p class="text-xs text-slate-500 mt-2">Consistent high output</p>
      </div>

      <!-- Key Focus Entity -->
      <div class="bg-slate-900 border border-slate-800 rounded-xl p-5 relative overflow-hidden group hover:border-slate-700 transition">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Key Focus Vessel</span>
          <div class="p-2 bg-teal-500/10 text-teal-400 rounded-lg">
            <i data-lucide="ship" class="w-5 h-5"></i>
          </div>
        </div>
        <div class="mt-3">
          <div class="text-lg font-bold text-white truncate">{{ currentAnalytics.summary.topVessel }}</div>
          <p class="text-xs text-teal-400 mt-0.5">Most linked entity</p>
        </div>
        <p class="text-xs text-slate-500 mt-2">Multiple connected records</p>
      </div>

    </section>

    <!-- MODULE BREAKDOWN & CHARTS SECTION -->
    <section class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      
      <!-- Module Cards Grid -->
      <div class="lg:col-span-2 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-base font-bold text-white flex items-center gap-2">
              <i data-lucide="pie-chart" class="w-4 h-4 text-blue-400"></i>
              Module Volume Breakdown
            </h2>
            <p class="text-xs text-slate-400">Distribution across 5 core operational domains</p>
          </div>
          <span *ngIf="selectedModuleFilter !== 'ALL'" class="text-xs bg-blue-900/60 text-blue-300 border border-blue-700 px-2.5 py-1 rounded-full flex items-center gap-1.5">
            <span>Filtered by: <strong>{{ selectedModuleFilter }}</strong></span>
            <button (click)="clearModuleFilter()" class="hover:text-white"><i data-lucide="x" class="w-3 h-3"></i></button>
          </span>
        </div>

        <!-- 5 Module Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div *ngFor="let modName of moduleList" 
               (click)="filterByModuleCard(modName)" 
               [class]="selectedModuleFilter === modName ? 'bg-slate-900 border border-blue-500 ring-1 ring-blue-500 rounded-xl p-3.5 cursor-pointer transition flex flex-col justify-between group' : 'bg-slate-900 border border-slate-800 rounded-xl p-3.5 hover:border-slate-700 cursor-pointer transition flex flex-col justify-between group'">
            
            <div class="flex items-start justify-between gap-2">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full" [style.backgroundColor]="moduleConfig[modName].color"></span>
                <h4 class="text-xs font-bold text-slate-200 group-hover:text-white">{{ modName }}</h4>
              </div>
              <span class="text-[10px] font-semibold text-slate-400 bg-slate-950 px-2 py-0.5 rounded border border-slate-800">{{ getModulePercentage(modName) }}%</span>
            </div>

            <div class="my-3 flex items-baseline justify-between">
              <span class="text-2xl font-extrabold text-white">{{ getModuleCount(modName) }}</span>
              <span class="text-[10px] text-slate-400">reports</span>
            </div>

            <div class="space-y-1.5">
              <div class="w-full bg-slate-950 rounded-full h-1.5 overflow-hidden">
                <div class="h-1.5 rounded-full" [style.width.%]="getModulePercentage(modName)" [style.backgroundColor]="moduleConfig[modName].color"></div>
              </div>
              <div class="flex items-center justify-between text-[10px] text-slate-500">
                <span>Last report: Today</span>
                <span class="text-blue-400 group-hover:underline">Filter &rarr;</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Timeline Bar Chart -->
        <div class="bg-slate-900 border border-slate-800 rounded-xl p-4 shadow-lg">
          <div class="flex items-center justify-between mb-3">
            <span class="text-xs font-bold uppercase tracking-wider text-slate-300">Activity Timeline Trend</span>
            <span class="text-xs text-slate-500">Weekly Activity Spread</span>
          </div>
          <div class="h-48 w-full">
            <canvas #timelineCanvas></canvas>
          </div>
        </div>
      </div>

      <!-- Ring Chart -->
      <div class="space-y-4">
        <div class="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-lg flex flex-col h-full justify-between">
          <div>
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-sm font-bold text-white">Module Distribution</h3>
              <i data-lucide="donut" class="w-4 h-4 text-slate-400"></i>
            </div>
            <div class="relative flex items-center justify-center py-2">
              <div class="w-48 h-48">
                <canvas #distributionCanvas></canvas>
              </div>
            </div>
          </div>

          <!-- Productivity Stats -->
          <div class="bg-slate-950 rounded-lg p-3 border border-slate-800/80 mt-4 space-y-2">
            <div class="text-xs font-semibold text-slate-400 uppercase tracking-wider">User Productivity Index</div>
            <div class="flex justify-between items-center text-xs">
              <span class="text-slate-400">Data Completion Rate</span>
              <span class="font-bold text-emerald-400">98.4%</span>
            </div>
            <div class="w-full bg-slate-800 h-1 rounded-full">
              <div class="bg-emerald-500 h-1 rounded-full" style="width: 98.4%"></div>
            </div>
            <div class="flex justify-between items-center text-xs pt-1">
              <span class="text-slate-400">Primary Format</span>
              <span class="font-medium text-slate-200">PDF Executive Reports</span>
            </div>
          </div>
        </div>
      </div>

    </section>

    <!-- ACTIVITY LOG TABLE -->
    <section class="bg-slate-900 border border-slate-800 rounded-xl shadow-xl overflow-hidden">
      <!-- Table Controls -->
      <div class="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 class="text-base font-bold text-white flex items-center gap-2">
            <i data-lucide="list" class="w-4 h-4 text-blue-400"></i>
            Generated Reports Audit Log
          </h2>
          <p class="text-xs text-slate-400">Detailed records generated by selected officer</p>
        </div>

        <div class="flex flex-wrap items-center gap-2.5">
          <!-- Search -->
          <div class="relative min-w-[200px]">
            <input type="text" [(ngModel)]="tableSearchQuery" (ngModelChange)="currentPage = 1" placeholder="Search report, vessel, ID..." class="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500">
          </div>

          <!-- Module Dropdown Filter -->
          <select [(ngModel)]="selectedModuleFilter" (ngModelChange)="currentPage = 1" class="bg-slate-950 border border-slate-800 text-xs text-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500">
            <option value="ALL">All Modules</option>
            <option *ngFor="let mod of moduleList" [value]="mod">{{ mod }}</option>
          </select>
        </div>
      </div>

      <!-- Table Body -->
      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-slate-950/60 text-slate-400 border-b border-slate-800 uppercase tracking-wider text-[10px]">
            <tr>
              <th class="py-3 px-4">Report ID</th>
              <th class="py-3 px-4">Module Domain</th>
              <th class="py-3 px-4">Report Name</th>
              <th class="py-3 px-4">Vessel / Location</th>
              <th class="py-3 px-4">Generated Date</th>
              <th class="py-3 px-4 text-center">Format</th>
              <th class="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-800/60">
            <tr *ngFor="let row of paginatedReports" class="hover:bg-slate-800/40 transition">
              <td class="py-3 px-4 font-mono font-semibold text-blue-400">{{ row.id }}</td>
              <td class="py-3 px-4">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border"
                      [ngClass]="[moduleConfig[row.module].bgClass, moduleConfig[row.module].textClass, moduleConfig[row.module].borderClass]">
                  {{ row.module }}
                </span>
              </td>
              <td class="py-3 px-4 font-medium text-slate-200">{{ row.title }}</td>
              <td class="py-3 px-4 text-slate-400">{{ row.vessel }}</td>
              <td class="py-3 px-4 text-slate-400 whitespace-nowrap">{{ row.timestamp | date:'mediumDate' }}</td>
              <td class="py-3 px-4 text-center">
                <span class="px-1.5 py-0.5 text-[10px] font-mono rounded bg-slate-800 border border-slate-700 text-slate-300">{{ row.format }}</span>
              </td>
              <td class="py-3 px-4 text-right space-x-2">
                <button (click)="openReportModal(row)" class="text-slate-400 hover:text-white transition">
                  <i data-lucide="eye" class="w-4 h-4 inline"></i>
                </button>
                <button (click)="triggerToast('Downloading ' + row.id)" class="text-slate-400 hover:text-blue-400 transition">
                  <i data-lucide="download" class="w-4 h-4 inline"></i>
                </button>
              </td>
            </tr>
            <tr *ngIf="paginatedReports.length === 0">
              <td colspan="7" class="py-8 text-center text-slate-500">No reports matching selected criteria.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Footer -->
      <div class="p-3 border-t border-slate-800 bg-slate-950/40 flex items-center justify-between text-xs text-slate-400">
        <div>Showing {{ filteredReports.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0 }} to {{ currentPage * itemsPerPage < filteredReports.length ? currentPage * itemsPerPage : filteredReports.length }} of {{ filteredReports.length }} entries</div>
        <div class="flex items-center gap-1.5">
          <button (click)="changePage(-1)" [disabled]="currentPage === 1" class="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded border border-slate-700 text-slate-200">Previous</button>
          <span class="px-2 font-medium text-slate-300">{{ currentPage }} / {{ totalPages }}</span>
          <button (click)="changePage(1)" [disabled]="currentPage >= totalPages" class="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded border border-slate-700 text-slate-200">Next</button>
        </div>
      </div>
    </section>

  </main>

  <!-- REPORT PREVIEW MODAL -->
  <div *ngIf="isReportModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
    <div class="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4 relative">
      <button (click)="closeReportModal()" class="absolute top-4 right-4 text-slate-400 hover:text-white">
        <i data-lucide="x" class="w-5 h-5"></i>
      </button>
      <div class="flex items-center gap-3">
        <div class="p-2 bg-blue-600/20 text-blue-400 rounded-lg">
          <i data-lucide="file-text" class="w-6 h-6"></i>
        </div>
        <div>
          <h3 class="text-base font-bold text-white">{{ selectedReportModal?.title }}</h3>
          <p class="text-xs text-slate-400">ID: {{ selectedReportModal?.id }}</p>
        </div>
      </div>
      <div class="border-t border-b border-slate-800 py-3 space-y-2 text-xs">
        <div class="flex justify-between"><span class="text-slate-400">Generated By:</span><span class="font-medium text-slate-200">{{ selectedUser.name }}</span></div>
        <div class="flex justify-between"><span class="text-slate-400">Module:</span><span class="font-medium text-slate-200">{{ selectedReportModal?.module }}</span></div>
        <div class="flex justify-between"><span class="text-slate-400">Vessel:</span><span class="font-medium text-slate-200">{{ selectedReportModal?.vessel }}</span></div>
        <div class="flex justify-between"><span class="text-slate-400">Date:</span><span class="font-medium text-slate-200">{{ selectedReportModal?.timestamp | date:'medium' }}</span></div>
      </div>
      <div class="flex justify-end gap-2 pt-2">
        <button (click)="closeReportModal()" class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-medium">Close</button>
      </div>
    </div>
  </div>

  <!-- API INSPECTOR MODAL -->
  <div *ngIf="isApiModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
    <div class="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl max-w-2xl w-full p-6 space-y-4 relative">
      <div class="flex items-center justify-between pb-3 border-b border-slate-800">
        <h3 class="text-base font-bold text-white">API Integration Contract & Data Schema</h3>
        <button (click)="toggleApiModal(false)" class="text-slate-400 hover:text-white"><i data-lucide="x" class="w-5 h-5"></i></button>
      </div>
      <pre class="bg-slate-950 p-3 rounded-lg border border-slate-800 text-[11px] font-mono text-emerald-400 overflow-x-auto">
GET /api/v1/reports/analytics?userId={{ selectedUser.id }}&timeframe={{ selectedTimeframe }}
      </pre>
      <div class="flex justify-end pt-2">
        <button (click)="toggleApiModal(false)" class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium">Close</button>
      </div>
    </div>
  </div>

  <!-- TOAST NOTIFICATION -->
  <div *ngIf="showToast" class="fixed bottom-5 right-5 z-50 bg-slate-900 border border-slate-700 text-slate-100 px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3">
    <i data-lucide="check-circle" class="w-5 h-5 text-emerald-400"></i>
    <span class="text-xs font-medium">{{ toastMessage }}</span>
  </div>

</div>